import React from 'react';
import { AlertTriangle, CheckCircle, XCircle, AlertCircle, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';

interface QueueHealthData {
  status: 'healthy' | 'degraded' | 'overloaded' | 'critical';
  queueDepth: number;
  processingRate: number;
  failureRate: number;
  oldestMessageAge: number;
  poisonMessageCount: number;
  trend: 'increasing' | 'decreasing' | 'stable';
  trendPercentage: number;
}

interface QueueHealthOverviewProps {
  health: QueueHealthData;
}

export const QueueHealthOverview: React.FC<QueueHealthOverviewProps> = ({ health }) => {
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle className="w-6 h-6 text-green-500" />;
      case 'degraded':
        return <AlertCircle className="w-6 h-6 text-yellow-500" />;
      case 'overloaded':
        return <AlertTriangle className="w-6 h-6 text-orange-500" />;
      case 'critical':
        return <XCircle className="w-6 h-6 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'bg-green-50 border-green-200 text-green-800';
      case 'degraded':
        return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      case 'overloaded':
        return 'bg-orange-50 border-orange-200 text-orange-800';
      case 'critical':
        return 'bg-red-50 border-red-200 text-red-800';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'increasing':
        return <TrendingUp className="w-4 h-4 text-red-500" />;
      case 'decreasing':
        return <TrendingDown className="w-4 h-4 text-green-500" />;
      case 'stable':
        return <Minus className="w-4 h-4 text-gray-500" />;
      default:
        return null;
    }
  };

  const formatAge = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
    return `${Math.floor(seconds / 86400)}d`;
  };

  return (
    <Card className={`border-2 ${getStatusColor(health.status)}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {getStatusIcon(health.status)}
            <div>
              <CardTitle className="text-xl">Queue Health</CardTitle>
              <p className="text-sm font-medium capitalize mt-1">{health.status}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {getTrendIcon(health.trend)}
            <span className="text-sm font-medium">
              {(health.trendPercentage || 0) > 0 ? '+' : ''}{health.trendPercentage || 0}%
            </span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-500">Queue Depth</p>
            <p className="text-lg font-semibold">{(health.queueDepth || 0).toLocaleString()}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Processing Rate</p>
            <p className="text-lg font-semibold">{(health.processingRate || 0)}/s</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Failure Rate</p>
            <p className="text-lg font-semibold">{(health.failureRate || 0).toFixed(1)}%</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Oldest Message</p>
            <p className="text-lg font-semibold">{formatAge(health.oldestMessageAge || 0)}</p>
          </div>
          {(health.poisonMessageCount || 0) > 0 && (
            <div className="col-span-2 pt-2 border-t">
              <p className="text-sm text-red-600 font-medium">
                ⚠️ {health.poisonMessageCount || 0} poison messages detected
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};